import React, { forwardRef, useContext, useRef } from 'react';
import type { FC } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { Popup as AKPopup } from '@atlaskit/popup';
import type { PopupComponentProps } from '@atlaskit/popup';
import type { Placement } from '@atlaskit/popper';
import { SpotlightTarget } from '@atlaskit/onboarding';

import { ConfluenceEdition } from '@confluence/change-edition';
import {
	ExperienceFailure,
	ExperienceSuccess,
	ExperienceTrackerContext,
	TRIAL_COMPONENTS_ACTION_MENU_VIEW_EXPERIENCE,
} from '@confluence/experience-tracker';
import { PremiumTrialOnboardingSpotlight } from '@confluence/premium-onboarding';
import { useSessionData } from '@confluence/session-data';
import { SPAViewContext } from '@confluence/spa-view-context';
import { useIsPremiumOnboardingFeatureDiscoveryEnabled } from '@confluence/premium-onboarding/entry-points/useIsPremiumOnboardingFeatureDiscoveryEnabled';
import { isTooManyRequestsError, isUnauthorizedError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import { PTO_PHASE_TWO_SPOTLIGHT_TARGET } from '@confluence/premium-onboarding/entry-points/useShouldCheckEligibilityForSpotlight';
import { useCanAddBillingForTrialProductUser } from '@confluence/experiment-more-product-users-can-upgrade-pay';
import {
	LazyAutoUpgradeAwarenessModal,
	useAutoUpgradeAwareness,
	AUTO_UPGRADE_AWARENESS_STANDARD_PILL_TOUCHPOINT,
} from '@confluence/experiment-auto-upgrade-awareness';
import { useConfluenceEADropdownExperimentAB } from '@confluence/experiment-confluence-ea-dropdown/entry-points/hooks';

import { useHasBillingInformation } from '../useHasBillingInformation';
import { useReverseTrialCohort } from '../useReverseTrialEligible';
import { TrialDetailsQuery } from '../graphql/TrialDetailsQuery.graphql';
import { ReverseTrialCohort } from '../graphql/__types__/SignUpProperties';
import { getTrialDaysLeftFromQuery } from '../graphql/getTrialDaysLeftFromQuery';
import type { TrialDetailsQuery as TrialDetailsQueryType } from '../graphql/__types__/TrialDetailsQuery';
import { useIsPreDunningPeriod } from '../useIsPreDunningPeriod';
import { useOpenActionMenu } from '../useOpenActionMenu';

import { ActionMenuPill } from './ActionMenuPill';
import { ActionMenuContent } from './ActionMenuContent';
export const EXPLORE_STANDARD_FEATURES_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/confluence-standard/';

// Styling for popup menu
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionMenuContainer = styled.div<{
	isReverseTrialEnrolledVersion?: boolean;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-array-arguments -- Ignored via go/DSP-18766
}>([
	{
		padding: token('space.025', '2px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		width: ({ isReverseTrialEnrolledVersion }) =>
			isReverseTrialEnrolledVersion ? '320px' : '310px',

		marginBottom: token('space.075', '6px'),
		borderRadius: '3px',
		boxShadow: token(
			'elevation.shadow.overflow',
			'0px 0px 1px rgba(9, 30, 66, 0.31), 0px 8px 12px rgba(9, 30, 66, 0.15)',
		),
		'&:focus-visible': {
			outline: 'none',
		},
	},
	// hide the popup via css under a certain width in navigation to account for delay from useWindowSize hook
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	() => ({
		display: 'none',
		'@media (min-width: 680px)': {
			display: 'initial',
		},
	}),
]);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PremiumEADropdownExperimentActionMenuContainer = styled(ActionMenuContainer)({
	backgroundColor: `${token('color.background.accent.gray.subtlest', '#F1F2F4')}`,
	width: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StandardActionMenuContainer = styled(ActionMenuContainer)({
	backgroundColor: `${token('color.background.accent.blue.subtler', '#CCE0FF')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PremiumActionMenuContainer = styled(ActionMenuContainer)({
	//  These are Teal200, Blue300, and Purple400 from new color palette
	backgroundImage: `linear-gradient(279.27deg,
    ${token('color.background.accent.teal.subtler', '#C1F0F5')} 7.53%,
    ${token('color.background.selected.pressed', '#85B8FF')} 51.24%,
    ${token('color.background.accent.purple.subtle', '#9F8FEF')} 93.5%)`,
});

const PTOPhaseTwoWrapper = ({ condition, children }) => {
	return condition ? (
		<SpotlightTarget name={PTO_PHASE_TWO_SPOTLIGHT_TARGET}>{children}</SpotlightTarget>
	) : (
		children
	);
};

type ActionMenuProps = {
	shouldHidePopup?: boolean;
};

export const ActionMenu: FC<ActionMenuProps> = ({ shouldHidePopup = false }) => {
	const [{ isOpen }, { setIsOpen }] = useOpenActionMenu();

	const { isSiteAdmin } = useContext(SPAViewContext);
	const { isLicensed, isLoggedIn, userId, edition } = useSessionData();

	const experienceTracker = useContext(ExperienceTrackerContext);
	const hasExperienceTrackerStartedRef = useRef<Boolean>(false);

	const {
		canAddBillingForTrial,
		isPreDunning,
		hasBillingDetails,
		analyticsAttributes: canAddBillingForTrialAnalyticsAttributes,
		loading: isCanAddBillingForTrialLoading,
		commerceError: canAddBillingForTrialError,
	} = useCanAddBillingForTrialProductUser();

	const { isAUAVariantEnabled } = useAutoUpgradeAwareness();

	const { isPremiumOnboardingFeatureDiscoveryEnabled, isPTORTEnabled } =
		useIsPremiumOnboardingFeatureDiscoveryEnabled({
			shouldCheckIsInTrial: false,
		});
	const shouldSkipTrialDetailsQueryForNonSiteAdmin =
		!isPremiumOnboardingFeatureDiscoveryEnabled && !isSiteAdmin && !canAddBillingForTrial;

	const { isPreDunningPeriod, loading: isPreDunningPeriodLoading } = useIsPreDunningPeriod({
		skip: !isSiteAdmin,
	});

	const isTrialEdition =
		edition === ConfluenceEdition.STANDARD || edition === ConfluenceEdition.PREMIUM;

	const skipTrialDetailsQuery =
		!isLicensed ||
		!isLoggedIn ||
		shouldSkipTrialDetailsQueryForNonSiteAdmin ||
		!userId ||
		!isTrialEdition;

	const {
		data: trialDetailsData,
		error: trialDetailsError,
		loading: trialDetailsLoading,
	} = useQuery<TrialDetailsQueryType>(TrialDetailsQuery, {
		skip: skipTrialDetailsQuery,
	});

	const daysLeft =
		!trialDetailsError && !trialDetailsLoading ? getTrialDaysLeftFromQuery(trialDetailsData) : 0;

	const isInTrial = daysLeft ? daysLeft > 0 : false;

	const skipBillingQuery = !isInTrial;
	const {
		hasBillingInformation: siteAdminHasBillingInformation,
		loading: billingInformationLoading,
		error: billingInformationError,
	} = useHasBillingInformation(skipBillingQuery);

	const hasBillingInformation = siteAdminHasBillingInformation || !!hasBillingDetails;

	const {
		reverseTrialCohort,
		loading: reverseTrialCohortLoading,
		error: reverseTrialCohortError,
		isReverseTrialEnrolledVersion,
	} = useReverseTrialCohort({
		skip: !isSiteAdmin && !canAddBillingForTrial,
	});

	const isInReverseTrialProductionizedGroup = reverseTrialCohort === ReverseTrialCohort.ENROLLED;

	const placement: Placement = isReverseTrialEnrolledVersion ? 'bottom-start' : 'bottom-end';

	const canTrackExperience = !skipBillingQuery && !skipTrialDetailsQuery;

	if (!hasExperienceTrackerStartedRef.current && canTrackExperience) {
		experienceTracker.start({
			name: TRIAL_COMPONENTS_ACTION_MENU_VIEW_EXPERIENCE,
		});
		hasExperienceTrackerStartedRef.current = true;
	}

	const isLoading =
		trialDetailsLoading ||
		billingInformationLoading ||
		isPreDunningPeriodLoading ||
		reverseTrialCohortLoading ||
		isCanAddBillingForTrialLoading;

	const error =
		trialDetailsError ||
		billingInformationError ||
		reverseTrialCohortError ||
		canAddBillingForTrialError;

	const isEligible =
		!!edition &&
		(isPreDunningPeriod || isInTrial) &&
		!hasBillingInformation &&
		!isLoading &&
		!error;

	const isProductUserEligible =
		!!edition &&
		(isPreDunning || isInTrial) &&
		// no billing info is one of the eligibility criteria for canAddBillingForTrial
		canAddBillingForTrial &&
		!isLoading &&
		!error;

	const shouldRenderEditionAwareness = isEligible || isProductUserEligible;

	// Premium trial onboarding experiment by cc-editions. Similar to ROI for Standard edition,
	// having billing information (hasBillingInformation) does NOT invalidate eligibility
	// for the PTO experiment variant cohort.
	const isEligiblePremiumTrialOnboardingExperiment =
		isPremiumOnboardingFeatureDiscoveryEnabled &&
		edition === ConfluenceEdition.PREMIUM &&
		isInTrial &&
		(!isPreDunningPeriod || (canAddBillingForTrial && !isPreDunning)) &&
		!isLoading &&
		!error;

	// Determines if conditions are met for rendering in app navigation context
	const willRenderInAppNavigation =
		isTrialEdition &&
		(shouldRenderEditionAwareness ||
			isPremiumOnboardingFeatureDiscoveryEnabled ||
			isInReverseTrialProductionizedGroup);

	// Main render condition function
	const canRenderHere = willRenderInAppNavigation;

	const shouldShowActionMenu =
		canRenderHere &&
		(isEligible || isProductUserEligible || isEligiblePremiumTrialOnboardingExperiment);

	const shouldShowAutoUpgradeAwareness =
		shouldShowActionMenu &&
		isAUAVariantEnabled &&
		(isSiteAdmin || canAddBillingForTrial) &&
		edition === ConfluenceEdition.STANDARD;

	// Moved variable up from inside the EA Dropdown if statement to use in our eligibility criteria for the confluence_ea_dropdown_ab experiment
	const shouldShowActionMenuForProductUsers =
		canAddBillingForTrial || isEligiblePremiumTrialOnboardingExperiment;

	// Moved variable up from inside the EA Dropdown if statement to use in our eligibility criteria for the confluence_ea_dropdown_ab experiment
	const shouldHideActionMenuForNonProductUsers =
		!isSiteAdmin && !shouldShowActionMenuForProductUsers;

	// Audience requirements for confluence_ea_dropdown_ab experiment
	const hasEligibilityCriteriaForEAPremiumDropdownExperiment =
		edition === ConfluenceEdition.PREMIUM && isInTrial;

	// Takes the rendering criteria for what shows the AKPopup at the bottom of the file and adds in the experiment criteria
	const isEligibleForEAPremiumDropdownExperiment =
		!error &&
		!isLoading &&
		!shouldShowAutoUpgradeAwareness &&
		shouldShowActionMenu &&
		!shouldHideActionMenuForNonProductUsers &&
		hasEligibilityCriteriaForEAPremiumDropdownExperiment;

	const { renderTreatment: renderTreatmentForEAPremiumDropdownExperiment } =
		useConfluenceEADropdownExperimentAB({
			skip: !isEligibleForEAPremiumDropdownExperiment,
			extraAnalytics: {
				// Will allow us to granularly see rendering + eligibility in monitoring
				shouldShowActionMenu,
				shouldHideActionMenuForNonProductUsers,
				shouldShowAutoUpgradeAwareness,
				shouldShowActionMenuForProductUsers,
				hasEligibilityCriteriaForEAPremiumDropdownExperiment,
				// User/tenant traits
				isSiteAdmin,
				edition,
				isInTrial,
				isPreDunning,
				hasBillingInformation,
				daysLeft,
				billingSourceSystem: trialDetailsData?.license?.billingSourceSystem,
			},
		});

	/* eslint-disable-next-line react/no-unstable-nested-components */
	const CustomPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(
		({ children, ...props }, ref) => {
			switch (edition) {
				case ConfluenceEdition.PREMIUM:
					if (renderTreatmentForEAPremiumDropdownExperiment) {
						return (
							// The PremiumActionMenuContainer has a multicolor gradient border which is not in our spec, therefore we are using our own styled container with a gray border
							<PremiumEADropdownExperimentActionMenuContainer
								{...props}
								ref={ref}
								isReverseTrialEnrolledVersion={isReverseTrialEnrolledVersion}
								data-testid="premium-ea-dropdown-experiment-action-menu-container"
							>
								{children}
							</PremiumEADropdownExperimentActionMenuContainer>
						);
					}

					return (
						<PremiumActionMenuContainer
							{...props}
							ref={ref}
							isReverseTrialEnrolledVersion={isReverseTrialEnrolledVersion}
							data-testid="premium-action-menu-container"
						>
							{children}
						</PremiumActionMenuContainer>
					);
				case ConfluenceEdition.STANDARD:
					return (
						<StandardActionMenuContainer {...props} ref={ref}>
							{children}
						</StandardActionMenuContainer>
					);
				default:
					return null;
			}
		},
	);

	if (error) {
		// If error is due to permission not granted for current user, we treat this as expected error
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
			experienceTracker.abort({
				name: TRIAL_COMPONENTS_ACTION_MENU_VIEW_EXPERIENCE,
				reason: 'Aborted because a pre-requisite was not met',
			});
			return null;
		}
		// If error is due to rate limiting, we treat this as expected error
		if (isTooManyRequestsError(error)) {
			markErrorAsHandled(error);
			experienceTracker.abort({
				name: TRIAL_COMPONENTS_ACTION_MENU_VIEW_EXPERIENCE,
				reason: 'Aborted because a rate limit exceeded error',
			});
			return null;
		}
		if (canAddBillingForTrialError) {
			markErrorAsHandled(error);
			experienceTracker.abort({
				name: TRIAL_COMPONENTS_ACTION_MENU_VIEW_EXPERIENCE,
				reason: 'Aborted because the query for can add billing for trial has an error',
			});
			return null;
		}
		return (
			<ExperienceFailure
				name={TRIAL_COMPONENTS_ACTION_MENU_VIEW_EXPERIENCE}
				error={error}
				attributes={{
					trialDetailsError: trialDetailsError?.message,
					billingInformationError: billingInformationError?.message,
					reverseTrialCohortError: reverseTrialCohortError?.message,
				}}
			/>
		);
	}
	if (shouldShowAutoUpgradeAwareness) {
		return (
			<>
				<ActionMenuPill
					hasBillingInformation={hasBillingInformation}
					triggerProps={{}}
					daysLeft={daysLeft}
					isOpen={isOpen}
					setIsOpen={setIsOpen}
					isPreDunningPeriod={isPreDunningPeriod || isPreDunning}
					canAddBillingForTrial={canAddBillingForTrial}
					canAddBillingForTrialAnalyticsAttributes={canAddBillingForTrialAnalyticsAttributes}
				/>
				{isOpen && (
					<LazyAutoUpgradeAwarenessModal
						touchpointId={AUTO_UPGRADE_AWARENESS_STANDARD_PILL_TOUCHPOINT}
						closeModal={() => setIsOpen(false)}
					/>
				)}
				<ExperienceSuccess name={TRIAL_COMPONENTS_ACTION_MENU_VIEW_EXPERIENCE} />
			</>
		);
	}

	if (shouldShowActionMenu) {
		if (shouldHideActionMenuForNonProductUsers) {
			return null;
		}

		return (
			<>
				<AKPopup
					/* eslint-disable-next-line react/no-unstable-nested-components */
					content={() => (
						<ActionMenuContent
							edition={edition}
							hasBillingInformation={hasBillingInformation}
							isPreDunningPeriod={isPreDunningPeriod || isPreDunning}
							trialEndDate={trialDetailsData?.license?.trialEndDate || undefined}
							isPremiumOnboardingFeatureDiscoveryEnabled={
								isPremiumOnboardingFeatureDiscoveryEnabled
							}
							canAddBillingForTrial={canAddBillingForTrial}
							canAddBillingForTrialAnalyticsAttributes={canAddBillingForTrialAnalyticsAttributes}
							isPTORTEnabled={isPTORTEnabled}
							billingSourceSystem={trialDetailsData?.license?.billingSourceSystem}
							isTreatmentForEaPremiumDropdownExperiment={
								renderTreatmentForEAPremiumDropdownExperiment
							}
							daysLeftInTrial={daysLeft}
						/>
					)}
					isOpen={isOpen && !shouldHidePopup}
					onClose={() => setIsOpen(false)}
					placement={placement}
					popupComponent={CustomPopupContainer}
					/* eslint-disable-next-line react/no-unstable-nested-components */
					trigger={(triggerProps) => (
						<PTOPhaseTwoWrapper
							condition={isPremiumOnboardingFeatureDiscoveryEnabled && !isPTORTEnabled}
						>
							<ActionMenuPill
								hasBillingInformation={hasBillingInformation}
								triggerProps={triggerProps}
								daysLeft={daysLeft}
								isOpen={isOpen}
								setIsOpen={setIsOpen}
								isPreDunningPeriod={isPreDunningPeriod || isPreDunning}
								canAddBillingForTrial={canAddBillingForTrial}
								canAddBillingForTrialAnalyticsAttributes={canAddBillingForTrialAnalyticsAttributes}
							/>
						</PTOPhaseTwoWrapper>
					)}
				/>
				{isPremiumOnboardingFeatureDiscoveryEnabled && (
					<PremiumTrialOnboardingSpotlight daysLeft={daysLeft} isSiteAdmin={isSiteAdmin} />
				)}
				<ExperienceSuccess name={TRIAL_COMPONENTS_ACTION_MENU_VIEW_EXPERIENCE} />
			</>
		);
	}

	return null;
};
